import { z } from "zod";

import {
  DAYS_OF_WEEK,
  type DayOfWeek,
} from "@/shared/domain/metrics/MetricRecord";

/**
 * A board's calendar: the time zone a board reads instants in, and the day
 * its weeks start on. Every time a board shows is that zone's wall-clock
 * time, and every calendar position it computes (a day, a week, an hour of
 * the day, a metric aggregate's period) is cut in that zone, with weeks
 * starting on `weekStart`.
 */
export interface BoardCalendar {
  /** An IANA time zone name, such as `America/Los_Angeles` or `UTC`. */
  timeZone: string;
  weekStart: DayOfWeek;
}

/** The calendar of a board whose definition declares none, and the value of
 *  each member a declared calendar leaves out: UTC, with weeks starting on
 *  Monday. These are the metric service's aggregate defaults, and Monday is
 *  the day DuckDB's `date_trunc('week')` truncates to. */
export const DEFAULT_BOARD_CALENDAR: BoardCalendar = {
  timeZone: "UTC",
  weekStart: "monday",
};

/**
 * Zone names `Intl` reads as zones with daylight saving time but the metric
 * service's database reads as fixed offsets, so the service refuses them.
 * Each maps to an area/location zone with the same offsets and rules, which
 * the refusal suggests. The entries copy the service's table of the same
 * name in `metrics_repo.py`.
 */
const ZONES_POSTGRES_READS_AS_FIXED_OFFSETS: Record<string, string> = {
  CET: "Europe/Paris",
  EET: "Europe/Athens",
  MET: "Europe/Paris",
  WET: "Europe/Lisbon",
};

const TIME_ZONE_NAME_ADVICE =
  'Use a time zone name such as "America/Los_Angeles" or "UTC".';

/**
 * Why `name` cannot be a board's time zone, or undefined when it can. A
 * board's zone is a name `Intl` knows, in the letter case `Intl` gives it.
 * It is neither an offset such as `+05:30`, which `Intl` accepts but DuckDB
 * and the metric service refuse, nor a name the metric service's database
 * reads as a fixed offset.
 */
export function timeZoneProblem(name: string): string | undefined {
  if (/^[+-]\d/.test(name)) {
    return `"${name}" is an offset from UTC, not a time zone. ${TIME_ZONE_NAME_ADVICE}`;
  }
  const areaLocationZone =
    ZONES_POSTGRES_READS_AS_FIXED_OFFSETS[name.toUpperCase()];
  if (areaLocationZone !== undefined) {
    return `A board can't use "${name}" as its time zone. Use a city's time zone instead, such as "${areaLocationZone}".`;
  }
  let resolved: string;
  try {
    resolved = new Intl.DateTimeFormat("en-US", {
      timeZone: name,
    }).resolvedOptions().timeZone;
  } catch {
    return `"${name}" is not a time zone. ${TIME_ZONE_NAME_ADVICE}`;
  }
  // `Intl` reads a name in any letter case; the metric service does not.
  if (resolved !== name && resolved.toLowerCase() === name.toLowerCase()) {
    return `Write the time zone "${name}" as "${resolved}", with its capital letters.`;
  }
  return undefined;
}

/** A board calendar's time zone name, refused with `timeZoneProblem`'s
 *  reason. */
export const boardTimeZoneSchema = z.string().superRefine((name, ctx) => {
  const problem = timeZoneProblem(name);
  if (problem !== undefined) {
    ctx.addIssue({ code: "custom", message: problem });
  }
});

/** A definition's `calendar` member as stored. Both members are optional;
 *  `boardCalendarOf` fills in the default of each one left out. */
export const declaredBoardCalendarSchema = z.strictObject({
  time_zone: boardTimeZoneSchema.optional(),
  week_start: z.enum(DAYS_OF_WEEK).optional(),
});

export type DeclaredBoardCalendar = z.infer<typeof declaredBoardCalendarSchema>;

/** The calendar a definition's `calendar` member declares, with the default
 *  of each member it leaves out. */
export function boardCalendarOf(
  declared: DeclaredBoardCalendar | undefined,
): BoardCalendar {
  return {
    timeZone: declared?.time_zone ?? DEFAULT_BOARD_CALENDAR.timeZone,
    weekStart: declared?.week_start ?? DEFAULT_BOARD_CALENDAR.weekStart,
  };
}

/** `calendar` written as a definition's `calendar` member, with both members
 *  set, as a saved definition and the chat's board context store it. */
export function declaredBoardCalendar(
  calendar: BoardCalendar,
): Required<DeclaredBoardCalendar> {
  return { time_zone: calendar.timeZone, week_start: calendar.weekStart };
}

export function sameBoardCalendar(
  first: BoardCalendar,
  second: BoardCalendar,
): boolean {
  return (
    first.timeZone === second.timeZone && first.weekStart === second.weekStart
  );
}

/** A weekday as 0 for Sunday to 6 for Saturday, as `Date.getUTCDay` and
 *  DuckDB's `dow` count. */
export function weekdayNumber(day: DayOfWeek): number {
  return (DAYS_OF_WEEK.indexOf(day) + 1) % 7;
}

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();
const shortNameFormatters = new Map<string, Intl.DateTimeFormat>();

function cachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  timeZone: string,
  create: () => Intl.DateTimeFormat,
): Intl.DateTimeFormat {
  let formatter = cache.get(timeZone);
  if (formatter === undefined) {
    formatter = create();
    cache.set(timeZone, formatter);
  }
  return formatter;
}

/** `GMT`, or `GMT` followed by a signed offset in hours, minutes and,
 *  before standard time zones existed, seconds: `GMT-07:00`, `GMT+05:30`. */
const LONG_OFFSET = /^GMT(?:([+-])(\d\d):(\d\d)(?::(\d\d))?)?$/;

/** How far `timeZone`'s clocks are ahead of UTC at the instant `ms`, in
 *  milliseconds: -25,200,000 in Los Angeles in summer. */
export function zoneOffsetMs(timeZone: string, ms: number): number {
  const formatter = cachedFormatter(
    offsetFormatters,
    timeZone,
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "longOffset",
      }),
  );
  const name =
    formatter.formatToParts(ms).find((part) => part.type === "timeZoneName")
      ?.value ?? "GMT";
  const match = LONG_OFFSET.exec(name);
  if (match?.[1] === undefined) {
    return 0;
  }
  const seconds =
    Number(match[2]) * 3600 + Number(match[3]) * 60 + Number(match[4] ?? 0);
  return (match[1] === "-" ? -seconds : seconds) * 1000;
}

/** The wall-clock time `timeZone`'s clocks show at the instant `ms`, written
 *  as milliseconds since the Unix epoch as if that time were UTC. */
export function wallClockMs(timeZone: string, ms: number): number {
  return ms + zoneOffsetMs(timeZone, ms);
}

/** The milliseconds in a day of 24 hours. */
export const MS_PER_DAY = 86_400_000;

/**
 * The instant at which `timeZone`'s clocks show the wall-clock time `wallMs`
 * (written as `wallClockMs` writes it). A time the clocks show twice, in the
 * hour they go back, is the later instant; a time they skip, in the hour
 * they go forward, is read with the offset in force before the skip, so it
 * lands after the skip. DuckDB's `timezone()` reads wall-clock times the same
 * way, so a board's bounds and its SQL agree.
 */
export function instantOfWallClock(timeZone: string, wallMs: number): number {
  const before = wallMs - zoneOffsetMs(timeZone, wallMs - MS_PER_DAY);
  const after = wallMs - zoneOffsetMs(timeZone, wallMs + MS_PER_DAY);
  const shows = (instant: number) => wallClockMs(timeZone, instant) === wallMs;
  if (shows(before) && shows(after)) {
    return Math.max(before, after);
  }
  return shows(after) ? after : before;
}

/** `timeZone`'s short name at the instant `ms` in the viewer's locale, as
 *  `Intl` writes it: `UTC`, `PDT`, `PST`, `GMT+2`. */
export function zoneShortName(timeZone: string, ms: number): string {
  const formatter = cachedFormatter(
    shortNameFormatters,
    timeZone,
    () =>
      new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: "short" }),
  );
  return (
    formatter.formatToParts(ms).find((part) => part.type === "timeZoneName")
      ?.value ?? timeZone
  );
}
